import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Dumbbell, Clock, Calendar, AlertTriangle, ChevronRight,
  X, Check, Loader2, LayoutGrid, Star, ChevronDown, ChevronUp,
  MapPin, Play, Zap, Pencil, Trash2, PlusCircle, RefreshCw, ShoppingBag,
} from "lucide-react";
import { useUserAuth, getUserToken } from "../context/UserAuthContext";
import UserNav from "../components/UserNav";
import GuidedWorkoutMode from "./GuidedWorkoutMode";
import RoutineBuilder from "./RoutineBuilder";
import { myRoutinesApi, formatApiError } from "../lib/api";

// Error Boundary to catch render crashes inside RoutineBuilder without black screen
class RoutineBuilderBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  componentDidCatch(e, info) { console.error("[RoutineBuilder crash]", e, info); }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center p-8 text-center">
          <AlertTriangle size={32} className="text-[#F2C230] mb-4" />
          <p className="font-black text-lg mb-2">Error al cargar el constructor</p>
          <p className="text-white/40 text-sm mb-6 max-w-xs">{this.state.error?.message || "Error desconocido"}</p>
          <button onClick={() => this.setState({ error: null })}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#F2C230] text-black font-black text-sm uppercase tracking-wider hover:bg-white transition-colors">
            <RefreshCw size={14} /> Reintentar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const API = process.env.REACT_APP_BACKEND_URL;
const NIVEL_LABELS = { 1: "Nivel 1 · Iniciación", 2: "Nivel 2 · Intermedio", 3: "Nivel 3 · Avanzado" };
const NIVEL_COLOR = { 1: "text-green-400", 2: "text-[#F2C230]", 3: "text-orange-400" };
const OBJETIVO_LABELS = {
  salud_general: "Salud general", ganar_musculo: "Ganar músculo", perder_grasa: "Perder grasa",
  ganar_fuerza: "Ganar fuerza", resistencia: "Resistencia", recuperacion_lesion: "Recuperación de lesión",
};
const EQUIPO_LABELS = {
  kettlebell: "Kettlebell", saco: "Saco", peso_propio: "Peso propio",
  maquina: "Máquina", libre: "Peso libre",
};
const EQUIPO_STYLE = {
  kettlebell:  "bg-orange-500/15 text-orange-300 border border-orange-500/25",
  saco:        "bg-amber-500/15 text-amber-300 border border-amber-500/25",
  peso_propio: "bg-blue-500/15 text-blue-300 border border-blue-500/25",
  maquina:     "bg-[#F2C230]/15 text-[#F2C230] border border-[#F2C230]/30",
  libre:       "bg-white/8 text-white/55 border border-white/12",
};

export default function MyRoutinePage() {
  const { user, loading, logout } = useUserAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [guidedDay, setGuidedDay] = useState(null);
  const [reminderPending, setReminderPending] = useState(null); // {day, exercises} while modal open

  const REMINDER_KEY = "xpro_reminder_last_shown";
  const handleStartGuided = (day, exs) => {
    const last = parseInt(localStorage.getItem(REMINDER_KEY) || "0");
    const fiveDaysMs = 5 * 24 * 60 * 60 * 1000;
    if (!last || Date.now() - last > fiveDaysMs) {
      setReminderPending({ day, exercises: exs });
    } else {
      setGuidedDay({ day, exercises: exs });
    }
  };
  const confirmReminder = () => {
    localStorage.setItem(REMINDER_KEY, String(Date.now()));
    setGuidedDay(reminderPending);
    setReminderPending(null);
  };
  // view: "mi-rutina" | "builder-create" | "builder-edit" | "catalogo"
  const [view, setView] = useState("mi-rutina");

  // Custom routine
  const [myCustomRoutine, setMyCustomRoutine] = useState(undefined); // undefined=loading
  const [savingRoutine, setSavingRoutine] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteErr, setDeleteErr] = useState("");

  const loadCustomRoutine = async () => {
    try {
      const r = await myRoutinesApi.getActive();
      setMyCustomRoutine(r || null);
    } catch { setMyCustomRoutine(null); }
  };
  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selecting, setSelecting] = useState(null); // routine_id being selected
  const [selectOk, setSelectOk] = useState(false);
  const [preview, setPreview] = useState(null); // routine shown in preview modal // { ejercicio_nombre, numero_maquina }
  // Catalog filters
  const [filterNivel, setFilterNivel] = useState("");
  const [filterObjetivo, setFilterObjetivo] = useState("");
  const [filterDias, setFilterDias] = useState("");

  const loadRoutine = () => {
    const token = getUserToken();
    fetch(`${API}/api/rutinas/mi-rutina`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then(setData).catch(() => setData(null)).finally(() => setFetching(false));
  };

  const loadCatalog = async () => {
    if (catalog.length > 0) return;
    setCatalogLoading(true);
    try {
      const r = await fetch(`${API}/api/rutinas/catalogo`, { headers: { Authorization: `Bearer ${getUserToken()}` } });
      const d = await r.json();
      setCatalog(Array.isArray(d) ? d : []);
    } catch { setCatalog([]); }
    finally { setCatalogLoading(false); }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    loadRoutine();
    loadCustomRoutine();
  }, [user, loading, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (view === "catalogo") loadCatalog();
  }, [view]);

  const handleSaveCustomRoutine = async (payload) => {
    setSavingRoutine(true);
    try {
      if (myCustomRoutine?.id) {
        const updated = await myRoutinesApi.update(myCustomRoutine.id, payload);
        setMyCustomRoutine(updated);
      } else {
        const created = await myRoutinesApi.create(payload);
        setMyCustomRoutine(created);
        await loadCustomRoutine(); // get enriched version
      }
      setView("mi-rutina");
    } catch (e) {
      alert(formatApiError(e?.response?.data?.detail) || "Error al guardar la rutina");
    } finally { setSavingRoutine(false); }
  };

  const handleDeleteCustomRoutine = async () => {
    if (!deleteConfirm) { setDeleteConfirm(true); return; }
    try {
      await myRoutinesApi.remove(myCustomRoutine.id);
      setMyCustomRoutine(null);
      setDeleteConfirm(false);
      setDeleteErr("");
    } catch (e) { setDeleteErr(formatApiError(e?.response?.data?.detail) || "Error al eliminar"); }
  };

  const handleSelectRoutine = async (routineId) => {
    setSelecting(routineId);
    try {
      const res = await fetch(`${API}/api/rutinas/seleccionar/${routineId}`, {
        method: "POST", headers: { Authorization: `Bearer ${getUserToken()}` },
      });
      if (res.ok) {
        setSelectOk(routineId);
        setFetching(true);
        loadRoutine();
        setTimeout(() => { setSelectOk(false); setView("mi-rutina"); }, 1500);
      }
    } catch { /* ignore */ }
    finally { setSelecting(null); }
  };

  if (loading || fetching || myCustomRoutine === undefined) return <LoadingScreen />;

  const handleLogout = () => { logout(); navigate("/login", { replace: true }); };

  // ── Builder views (full-page, no tabs) ─────────────────────────────────────
  if (view === "builder-create" || view === "builder-edit") {
    const editData = view === "builder-edit" && myCustomRoutine ? {
      id: myCustomRoutine.id,
      nombre: myCustomRoutine.nombre,
      dias_por_semana: myCustomRoutine.dias_por_semana,
      ejercicios: (myCustomRoutine.ejercicios || []).map((ej) => ({
        dia: ej.dia,
        orden: ej.orden,
        nombre: ej.nombre,
        numero_maquina: ej.numero_maquina || null,
        series: ej.series,
        repeticiones: String(ej.repeticiones),
      })),
    } : null;
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <RoutineBuilderBoundary>
          <RoutineBuilder
            initialData={editData}
            onSave={handleSaveCustomRoutine}
            onCancel={() => setView("mi-rutina")}
            saving={savingRoutine}
          />
        </RoutineBuilderBoundary>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="my-routine-page">
      <UserNav user={user} onLogout={handleLogout} />

      <div className="max-w-lg mx-auto px-4 py-8 pb-16">
        <p className="text-white/40 text-sm mb-4">Hola, {user?.nombre_completo?.split(" ")[0]}</p>

        {/* View toggle */}
        <div className="flex border border-white/10 mb-7">
          <button onClick={() => setView("mi-rutina")} data-testid="tab-mi-rutina"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-black tracking-[0.15em] uppercase transition-colors ${view === "mi-rutina" ? "bg-[#F2C230] text-black" : "text-white/40 hover:text-white"}`}>
            <Dumbbell size={12} /> Mi Rutina
          </button>
          <button onClick={() => setView("catalogo")} data-testid="tab-catalogo"
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-black tracking-[0.15em] uppercase transition-colors ${view === "catalogo" ? "bg-[#F2C230] text-black" : "text-white/40 hover:text-white"}`}>
            <LayoutGrid size={12} /> Catálogo
          </button>
        </div>

        {/* ====== MI RUTINA ACTUAL ====== */}
        {view === "mi-rutina" && (
          <>
            {/* ── Rutina personalizada del usuario (prioridad) ── */}
            {myCustomRoutine ? (
              <>
                {/* Header de rutina propia */}
                <div className="border border-[#F2C230]/20 bg-[#F2C230]/4 px-4 py-3 mb-4 flex items-center justify-between gap-3" data-testid="custom-routine-header">
                  <div>
                    <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230] mb-0.5">Tu rutina personalizada</p>
                    <p className="text-xs text-white/45">Visible solo para ti · Sustituye la del gym mientras esté activa</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setView("builder-edit")} data-testid="edit-custom-routine-btn"
                      className="w-8 h-8 flex items-center justify-center border border-white/12 text-white/40 hover:text-[#F2C230] hover:border-[#F2C230]/30 transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button onClick={handleDeleteCustomRoutine} data-testid="delete-custom-routine-btn"
                      className={`w-8 h-8 flex items-center justify-center border transition-colors ${deleteConfirm ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : "border-white/12 text-white/40 hover:text-red-400 hover:border-red-500/30"}`}>
                      {deleteConfirm ? <Check size={13} strokeWidth={3} /> : <Trash2 size={13} />}
                    </button>
                    {deleteConfirm && (
                      <button onClick={() => setDeleteConfirm(false)} className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white transition-colors">
                        <X size={13} />
                      </button>
                    )}
                  </div>
                </div>
                {deleteConfirm && (
                  <p className="text-xs text-red-300/80 mb-3 -mt-2">Pulsa el tick para confirmar el borrado permanente.</p>
                )}
                {deleteErr && <p className="text-red-400 text-xs mb-3">{deleteErr}</p>}
                <RoutineView
                  rutina={myCustomRoutine}
                  fechaAsignacion={null}
                  onBrowse={() => setView("catalogo")}
                  onStartGuided={(day, exs) => handleStartGuided(day, exs)}
                />
                {/* ── Banner tienda suplementos ── */}
                <Link to="/tienda"
                  className="mt-6 flex items-center justify-between gap-3 border border-[#F2C230]/20 bg-[#F2C230]/4 px-4 py-3 hover:bg-[#F2C230]/8 transition-colors group"
                  data-testid="suplementos-banner">
                  <div className="flex items-center gap-3">
                    <ShoppingBag size={15} className="text-[#F2C230] shrink-0" />
                    <div>
                      <p className="text-xs font-black tracking-wide text-white">Compra tus suplementos</p>
                      <p className="text-[11px] text-white/40">Top marca al mejor precio en XPRO</p>
                    </div>
                  </div>
                  <ChevronRight size={15} className="text-[#F2C230]/50 group-hover:text-[#F2C230] transition-colors shrink-0" />
                </Link>
              </>
            ) : (
              <>
                {/* ── Rutina asignada por el gym ── */}
                {data?.estado === "pendiente_revision" && (
                  <div data-testid="pending-review">
                    <div className="w-14 h-14 rounded-full bg-[#F2C230]/10 border border-[#F2C230]/30 flex items-center justify-center mb-5">
                      <AlertTriangle size={24} className="text-[#F2C230]" />
                    </div>
                    <h1 className="text-2xl font-black mb-3">Cuenta en revisión</h1>
                    <p className="text-white/55 text-sm leading-relaxed max-w-xs">El equipo de XPRO FITNESS se pondrá en contacto contigo antes de asignarte una rutina.</p>
                  </div>
                )}
                {data?.estado === "sin_cuestionario" && (
                  <div data-testid="no-questionnaire">
                    <h1 className="text-2xl font-black mb-3">Completa tu perfil</h1>
                    <p className="text-white/55 text-sm mb-6">Antes de asignarte una rutina necesitamos conocer tu estado de salud y objetivos.</p>
                    <Link to="/onboarding/seguridad" className="inline-flex items-center gap-2 bg-[#F2C230] text-black font-black text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-white transition-colors">
                      Completar cuestionario <ChevronRight size={16} />
                    </Link>
                  </div>
                )}
                {data?.estado === "asignada" && data.rutina && (
                  <RoutineView
                    rutina={data.rutina}
                    fechaAsignacion={data.fecha_asignacion}
                    onBrowse={() => setView("catalogo")}
                    onStartGuided={(day, exs) => handleStartGuided(day, exs)}
                  />
                )}
                {data?.estado === "sin_rutina" && (
                  <div data-testid="no-routine">
                    <h1 className="text-2xl font-black mb-3">Aún sin rutina</h1>
                    <p className="text-white/55 text-sm mb-5">Elige una del catálogo o espera a que el equipo te asigne una personalizada.</p>
                    <button onClick={() => setView("catalogo")} className="inline-flex items-center gap-2 bg-[#F2C230] text-black font-black text-xs tracking-[0.2em] uppercase px-6 py-4 hover:bg-white transition-colors">
                      Ver catálogo <ChevronRight size={16} />
                    </button>
                  </div>
                )}

                {/* Create custom routine button */}
                <div className="mt-8 border-t border-white/6 pt-6">
                  <p className="text-[10px] font-black tracking-[0.2em] uppercase text-white/30 mb-3">¿Quieres entrenar a tu manera?</p>
                  <button onClick={() => setView("builder-create")} data-testid="create-custom-routine-btn"
                    className="w-full flex items-center justify-center gap-2 border border-white/12 text-white/50 font-black text-xs tracking-[0.15em] uppercase py-4 hover:border-[#F2C230]/40 hover:text-[#F2C230] transition-colors">
                    <PlusCircle size={14} /> Crear mi rutina personalizada
                  </button>
                </div>
              </>
            )}

            {/* ── Banner tienda suplementos ── */}
            <Link to="/tienda"
              className="mt-6 flex items-center justify-between gap-3 border border-[#F2C230]/20 bg-[#F2C230]/4 px-4 py-3 hover:bg-[#F2C230]/8 transition-colors group"
              data-testid="suplementos-banner">
              <div className="flex items-center gap-3">
                <ShoppingBag size={15} className="text-[#F2C230] shrink-0" />
                <div>
                  <p className="text-xs font-black tracking-wide text-white">Compra tus suplementos</p>
                  <p className="text-[11px] text-white/40">Top marca al mejor precio en XPRO</p>
                </div>
              </div>
              <ChevronRight size={15} className="text-[#F2C230]/50 group-hover:text-[#F2C230] transition-colors shrink-0" />
            </Link>

          </>
        )}

        {/* ====== CATÁLOGO RUTINAS ESTÁNDAR ====== */}
        {view === "catalogo" && (
          <div data-testid="rutinas-catalog">
            <div className="mb-5">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black mb-1">Disponibles para ti</p>
              <h2 className="text-2xl font-black">Catálogo de Rutinas</h2>
              <p className="text-white/35 text-xs mt-1">Elige la que mejor se adapte a tu objetivo. Puedes cambiarla cuando quieras.</p>
            </div>

            {/* Filters */}
            <div className="flex gap-2 mb-4 flex-wrap">
              <select value={filterNivel} onChange={(e) => setFilterNivel(e.target.value)} data-testid="filter-nivel"
                className="bg-black border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#F2C230] transition-colors appearance-none">
                <option value="">Todos los niveles</option>
                <option value="1">Iniciación</option>
                <option value="2">Intermedio</option>
                <option value="3">Avanzado</option>
              </select>
              <select value={filterObjetivo} onChange={(e) => setFilterObjetivo(e.target.value)} data-testid="filter-objetivo"
                className="bg-black border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#F2C230] transition-colors appearance-none">
                <option value="">Todos los objetivos</option>
                {Object.entries(OBJETIVO_LABELS).filter(([k]) => k !== "recuperacion_lesion").map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <select value={filterDias} onChange={(e) => setFilterDias(e.target.value)} data-testid="filter-dias"
                className="bg-black border border-white/10 text-white text-xs px-3 py-2 outline-none focus:border-[#F2C230] transition-colors appearance-none">
                <option value="">Todos los días</option>
                <option value="3">3 días/sem</option>
                <option value="4">4 días/sem</option>
                <option value="5">5 días/sem</option>
              </select>
              {(filterNivel || filterObjetivo || filterDias) && (
                <button onClick={() => { setFilterNivel(""); setFilterObjetivo(""); setFilterDias(""); }}
                  data-testid="clear-filters" className="text-xs text-white/35 hover:text-white transition-colors px-2">
                  <X size={13} />
                </button>
              )}
            </div>

            {catalogLoading ? (
              <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-white/30" /></div>
            ) : catalog.length === 0 ? (
              <div className="border border-white/8 p-10 text-center text-white/30 text-sm">No hay rutinas estándar disponibles aún.</div>
            ) : (() => {
              const filtered = catalog.filter((r) => {
                if (filterNivel && String(r.nivel) !== filterNivel) return false;
                if (filterObjetivo && r.objetivo !== filterObjetivo) return false;
                if (filterDias && String(r.dias_por_semana) !== filterDias) return false;
                return true;
              });
              return filtered.length === 0 ? (
                <div className="border border-white/8 p-8 text-center text-white/30 text-sm">Sin resultados para estos filtros.</div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((r) => (
                    <RoutineCatalogCard
                      key={r.id} routine={r}
                      isCurrent={data?.rutina?.id === r.id}
                      selecting={selecting === r.id}
                      selectOk={selectOk === r.id}
                      onSelect={() => handleSelectRoutine(r.id)}
                      onPreview={() => setPreview(r)}
                    />
                  ))}
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* ── Reminder modal ── */}
      {reminderPending && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm p-4" data-testid="reminder-modal">
          <div className="w-full max-w-sm bg-[#0d0d0d] border border-[#F2C230]/30 shadow-2xl">
            {/* Header */}
            <div className="bg-[#F2C230] px-5 py-4">
              <p className="text-[10px] font-black tracking-[0.2em] uppercase text-black/60 mb-0.5">Aviso importante</p>
              <p className="text-sm font-black text-black leading-snug">
                Desde Xpro queremos hacer un recordatorio con el fin de mantener el centro limpio y ordenado.
              </p>
            </div>
            {/* Body */}
            <div className="px-5 py-5 space-y-3">
              <div className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F2C230] mt-1.5 shrink-0" />
                <p className="text-sm text-white/85">Es obligatorio el uso de toalla.</p>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F2C230] mt-1.5 shrink-0" />
                <p className="text-sm text-white/85">Quitar los discos después de cada ejercicio.</p>
              </div>
              <p className="text-sm text-white/50 pt-1">Gracias,</p>
            </div>
            {/* CTA */}
            <div className="px-5 pb-5">
              <button onClick={confirmReminder} data-testid="reminder-confirm-btn"
                className="w-full flex items-center justify-center gap-2 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.2em] uppercase py-3.5 hover:bg-white transition-colors">
                <Play size={12} fill="currentColor" /> Comenzar entrenamiento
              </button>
            </div>
          </div>
        </div>
      )}

      {guidedDay && (
        <GuidedWorkoutMode
          day={guidedDay.day}
          exercises={guidedDay.exercises}
          onClose={() => setGuidedDay(null)}
        />
      )}
      {preview && <RoutinePreviewModal routine={preview} isCurrent={data?.rutina?.id === preview.id} onSelect={() => handleSelectRoutine(preview.id)} selecting={selecting === preview.id} selectOk={selectOk === preview.id} onClose={() => setPreview(null)} />}
    </div>
  );
}

function RoutineView({ rutina, fechaAsignacion, onBrowse, onStartGuided }) {
  const isPersonalizada = rutina.tipo === "personalizada" || rutina.tipo === "personalizada_usuario";
  const isUserOwned = rutina.tipo === "personalizada_usuario";
  const byDay = {};
  for (const ej of rutina.ejercicios || []) {
    if (!byDay[ej.dia]) byDay[ej.dia] = [];
    byDay[ej.dia].push(ej);
  }
  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  return (
    <div data-testid="routine-view">
      {/* Badge personalizada */}
      {isPersonalizada && !isUserOwned && (
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-500/10 border border-purple-500/25 text-purple-300 text-[10px] font-black tracking-[0.2em] uppercase mb-3" data-testid="personalizada-badge">
          <Star size={10} fill="currentColor" /> Personalizada para ti
        </div>
      )}

      <h1 className="text-2xl font-black mb-1">{rutina.nombre}</h1>
      <div className="flex flex-wrap gap-2 mb-4 mt-3">
        {rutina.nivel && <Tag icon={<Dumbbell size={11} />} label={NIVEL_LABELS[rutina.nivel] || `Nivel ${rutina.nivel}`} />}
        <Tag icon={<Calendar size={11} />} label={`${rutina.dias_por_semana} días/semana`} />
        {rutina.objetivo && <Tag icon={<Clock size={11} />} label={OBJETIVO_LABELS[rutina.objetivo] || rutina.objetivo?.replace(/_/g, " ")} />}
      </div>

      <div className="space-y-4">
        {days.map((dia) => {
          const sortedExs = byDay[dia].slice().sort((a, b) => (a.orden || 0) - (b.orden || 0));
          return (
            <div key={dia} className="border border-white/8 overflow-hidden">
              <div className="bg-white/4 px-4 py-2.5 border-b border-white/8 flex items-center justify-between">
                <p className="text-xs font-black tracking-[0.2em] uppercase text-[#F2C230]">Día {dia}</p>
                <button
                  onClick={() => onStartGuided(dia, sortedExs)}
                  data-testid={`start-guided-day-${dia}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#F2C230] text-black font-black text-[10px] tracking-[0.15em] uppercase hover:bg-white transition-colors">
                  <Play size={10} fill="currentColor" /> Entrenar
                </button>
              </div>
              <div className="divide-y divide-white/5">
                {sortedExs.map((ej, i) => (
                  <ExerciseRow key={i} ej={ej} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {fechaAsignacion && (
        <p className="text-[10px] text-white/25 mt-6">
          Rutina asignada el {new Date(fechaAsignacion).toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" })}
        </p>
      )}
    </div>
  );
}

function getYouTubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?rel=0&playsinline=1` : null;
}

function ExerciseRow({ ej }) {
  const [expanded, setExpanded] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const hasMachine = ej.numero_maquina != null;
  const equipoLabel = EQUIPO_LABELS[ej.equipo_necesario] || ej.equipo_necesario;
  const hasMedia = ej.foto_url || ej.video_url;
  const hasInfo = ej.instrucciones_breves;
  const embedUrl = getYouTubeEmbed(ej.video_url);

  const grupos = Array.isArray(ej.grupo_muscular)
    ? ej.grupo_muscular.join(" · ")
    : ej.grupo_muscular || null;

  return (
    <div className="border-b border-white/6 last:border-0">
      {/* Main row */}
      <div className="flex items-start gap-3 px-4 py-4">
        {/* Machine number or equipo badge */}
        <div className="shrink-0 pt-0.5">
          {hasMachine ? (
            <Link to={`/maquina/${ej.numero_maquina}`}
              data-testid={`machine-link-${ej.numero_maquina}`}
              className="flex flex-col items-center justify-center w-11 h-11 bg-[#F2C230] text-black hover:bg-white transition-colors">
              <span className="font-black text-xs leading-none">MÁQ</span>
              <span className="font-black text-base leading-none">{ej.numero_maquina}</span>
            </Link>
          ) : (
            <div className="w-11 h-11 flex items-center justify-center border border-white/15 bg-white/5">
              <Dumbbell size={16} className="text-white/40" />
            </div>
          )}
        </div>

        {/* Info block */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-white leading-snug">{ej.nombre_maquina || ej.exercise_id}</p>
          {grupos && (
            <div className="flex items-center gap-1 mt-1">
              <Zap size={10} className="text-[#F2C230] shrink-0" />
              <span className="text-[11px] text-[#F2C230] font-bold">{grupos}</span>
            </div>
          )}
          {ej.planta && (
            <div className="flex items-center gap-1 mt-0.5">
              <MapPin size={10} className="text-white/55 shrink-0" />
              <span className="text-[11px] text-white/65 font-medium">{ej.planta}</span>
            </div>
          )}
          {!hasMachine && equipoLabel && (
            <span className="inline-block mt-1 text-[11px] text-white/60 border border-white/20 px-2 py-0.5 font-medium">{equipoLabel}</span>
          )}
          {(hasMedia || hasInfo) && (
            <button onClick={() => setExpanded(v => !v)}
              data-testid={`expand-btn-${ej.exercise_id}`}
              className="flex items-center gap-1 mt-2 text-[11px] text-white/50 hover:text-[#F2C230] transition-colors font-bold tracking-wider uppercase">
              {expanded ? <><ChevronUp size={11} /> Ocultar</> : <><ChevronDown size={11} /> Cómo hacerlo</>}
            </button>
          )}
        </div>

        {/* Series × reps (no log button) */}
        <div className="shrink-0 text-right">
          <span className="text-base font-black text-white tabular-nums">{ej.series}×{ej.repeticiones}</span>
          <p className="text-[11px] text-white/55 font-medium">series</p>
        </div>
      </div>

      {/* Expanded: instrucciones + foto + video */}
      {expanded && (
        <div className="mx-4 mb-4 p-3 bg-white/[0.03] border border-white/8 space-y-3">
          {ej.foto_url && (
            <img src={ej.foto_url} alt={ej.nombre_maquina}
              className="w-full max-h-44 object-cover rounded"
              data-testid={`exercise-foto-${ej.exercise_id}`} />
          )}
          {hasInfo && (
            <p className="text-sm text-white/80 leading-relaxed">{ej.instrucciones_breves}</p>
          )}
          {ej.video_url && (
            <div>
              <button onClick={() => setVideoOpen(v => !v)}
                data-testid={`exercise-video-${ej.exercise_id}`}
                className="inline-flex items-center gap-2 text-[#F2C230] text-xs font-bold hover:text-white transition-colors">
                <Play size={12} fill="currentColor" />
                {videoOpen ? "Ocultar vídeo" : "Ver vídeo tutorial"}
              </button>
              {videoOpen && (
                embedUrl ? (
                  <div className="mt-2 w-full max-w-[220px] mx-auto">
                    <div className="relative aspect-[9/16] overflow-hidden rounded-lg bg-black ring-1 ring-white/10">
                      <iframe src={embedUrl} title="Tutorial"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen className="absolute inset-0 w-full h-full" />
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 w-full aspect-video bg-black rounded overflow-hidden ring-1 ring-white/10">
                    <iframe src={ej.video_url} title="Tutorial" allowFullScreen className="w-full h-full" />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tag({ icon, label }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 text-[11px] text-white/65 font-bold tracking-widest uppercase">
      {icon}{label}
    </span>
  );
}

const NIVEL_SHORT = { 1: "Iniciación", 2: "Intermedio", 3: "Avanzado" };
const NIVEL_BADGE = {
  1: "text-green-400 bg-green-500/10 border-green-500/20",
  2: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  3: "text-orange-400 bg-orange-500/10 border-orange-500/20",
};

function RoutineCatalogCard({ routine, isCurrent, selecting, selectOk, onSelect, onPreview }) {
  return (
    <div className={`border ${isCurrent ? "border-[#F2C230]/50 bg-[#F2C230]/3" : "border-white/8"} p-4 transition-colors`} data-testid={`catalog-card-${routine.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border ${NIVEL_BADGE[routine.nivel] || "text-white/40 bg-white/5 border-white/10"}`}>
              {NIVEL_SHORT[routine.nivel] || `Nivel ${routine.nivel}`}
            </span>
            {isCurrent && (
              <span className="text-[9px] font-black tracking-widest uppercase px-2 py-0.5 bg-[#F2C230]/15 text-[#F2C230] border border-[#F2C230]/30" data-testid={`current-badge-${routine.id}`}>
                Activa
              </span>
            )}
          </div>
          <p className="text-sm font-bold text-white">{routine.nombre}</p>
          <p className="text-[11px] text-white/40 mt-0.5">{routine.dias_por_semana} días/semana · {routine.ejercicios?.length || 0} ejercicios</p>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          <button
            onClick={onSelect}
            disabled={selecting || isCurrent}
            data-testid={`select-routine-${routine.id}`}
            className={`flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-wider uppercase transition-colors disabled:opacity-50 ${
              selectOk ? "bg-green-500/20 text-green-400 border border-green-500/30" :
              isCurrent ? "bg-[#F2C230]/10 text-[#F2C230] border border-[#F2C230]/30" :
              "bg-[#F2C230] text-black hover:bg-white"
            }`}>
            {selecting ? <Loader2 size={11} className="animate-spin" /> : (selectOk || isCurrent) ? <Check size={11} /> : null}
            {selectOk ? "Seleccionada" : isCurrent ? "Activa" : "Elegir"}
          </button>
          <button onClick={onPreview} data-testid={`preview-routine-${routine.id}`}
            className="px-3 py-1.5 text-[10px] font-bold tracking-wider uppercase border border-white/10 text-white/45 hover:border-white/30 hover:text-white transition-colors text-center">
            Ver rutina
          </button>
        </div>
      </div>
    </div>
  );
}

function RoutinePreviewModal({ routine, isCurrent, onSelect, selecting, selectOk, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  const byDay = {};
  for (const ej of routine.ejercicios || []) {
    if (!byDay[ej.dia]) byDay[ej.dia] = [];
    byDay[ej.dia].push(ej);
  }
  const days = Object.keys(byDay).map(Number).sort((a, b) => a - b);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" data-testid="routine-preview-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[85vh] flex flex-col">
        <div className="border-b border-white/8 px-5 py-4 flex items-center justify-between shrink-0">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#F2C230] font-black">Vista previa</p>
            <p className="text-sm font-bold text-white mt-0.5">{routine.nombre}</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors"><X size={16} /></button>
        </div>
        <div className="overflow-y-auto overscroll-contain flex-1 p-5 space-y-3">
          <div className="flex flex-wrap gap-2 mb-2">
            <Tag icon={<Dumbbell size={11} />} label={NIVEL_LABELS[routine.nivel] || `Nivel ${routine.nivel}`} />
            <Tag icon={<Calendar size={11} />} label={`${routine.dias_por_semana} días/semana`} />
          </div>
          {days.length === 0 && (
            <p className="text-white/30 text-sm text-center py-6">Esta rutina no tiene ejercicios aún.</p>
          )}
          {days.map((dia) => (
            <div key={dia} className="border border-white/8">
              <div className="bg-white/4 px-3 py-2 border-b border-white/8">
                <p className="text-[10px] font-black tracking-[0.2em] uppercase text-[#F2C230]">Día {dia}</p>
              </div>
              <div className="divide-y divide-white/5">
                {byDay[dia].sort((a, b) => (a.orden || 0) - (b.orden || 0)).map((ej, i) => {
                  const hasMachine = ej.numero_maquina != null;
                  const equipoLabel = EQUIPO_LABELS[ej.equipo_necesario] || ej.equipo_necesario;
                  const equipoStyle = EQUIPO_STYLE[ej.equipo_necesario] || "bg-white/8 text-white/55 border border-white/12";
                  return (
                    <div key={i} className="flex items-center justify-between px-3 py-2.5 gap-2">
                      <div className="flex items-center gap-2.5 min-w-0">
                        {hasMachine ? (
                          <span className="bg-[#F2C230] text-black font-black text-[10px] px-1.5 py-0.5 shrink-0">{ej.numero_maquina}</span>
                        ) : (
                          <span className={`font-black text-[9px] px-1.5 py-0.5 shrink-0 ${equipoStyle}`}>{equipoLabel}</span>
                        )}
                        <div className="min-w-0">
                          <span className="text-sm text-white/75 font-medium truncate block">{ej.nombre_maquina || ej.exercise_id}</span>
                          {ej.planta && <span className="text-[10px] text-white/30">{ej.planta}</span>}
                        </div>
                      </div>
                      <span className="text-xs font-black text-white shrink-0">{ej.series} × {ej.repeticiones}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-white/8 px-5 py-4 shrink-0">
          <button
            onClick={onSelect}
            disabled={selecting || isCurrent}
            data-testid="preview-select-btn"
            className={`w-full flex items-center justify-center gap-2 font-black text-[11px] tracking-[0.25em] uppercase py-3 transition-colors disabled:opacity-40 ${
              selectOk || isCurrent ? "bg-green-500/20 text-green-400 border border-green-500/30" : "bg-[#F2C230] text-black hover:bg-white"
            }`}>
            {selecting ? <Loader2 size={13} className="animate-spin" /> : (selectOk || isCurrent) ? <Check size={13} /> : null}
            {selectOk ? "¡Seleccionada!" : isCurrent ? "Rutina activa" : "Elegir esta rutina"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="routine-loading">
      <div className="w-8 h-8 border-2 border-[#F2C230] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}
